// import APIs
import { CRON_JOBS } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

// import components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { CronJobsCRUDForm } from './CronJobsCRUDForm';

// import external libraries
import React from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useGetCronJobs } from 'queries';
import { useChangeRunStatus } from './hooks/useChangeRunStatus';
import useColumnDefinitions from './hooks/useColumnDefinitions';

// import types
import { CronJob } from './CronJobsCRUDForm.types';
import { GridRowId } from '@mui/x-data-grid-pro';
import { CronJobsCRUDProps } from './CronJobsCRUD.types';

const CronJobsCRUD: React.FC<CronJobsCRUDProps> = ({
  schema,
  convertData,
  children,
  defaultFormValues
}) => {
  const { t } = useTranslation();
  const { data, isFetching } = useGetCronJobs();
  const columnDefinitions = useColumnDefinitions();

  const { mutate: deleteCronJob } = useDeleteById(CRON_JOBS, [
    'cron-jobs',
    'list'
  ]);

  const { mutate: handlePause } = useChangeRunStatus();

  const handlePauseClick = (id: GridRowId, row?: CronJob) => {
    handlePause({ id, row });
  };

  const handleDelete = (row?: CronJob) => {
    const url = `${row?.group}/${row?.jobName}`;
    deleteCronJob(url);
  };

  return (
    <TableCRUDLayout
      FormComponent={({ handleClose }) => (
        <CronJobsCRUDForm
          onClose={handleClose}
          schema={schema}
          convertData={convertData}
          defaultValues={defaultFormValues}
        >
          {children}
        </CronJobsCRUDForm>
      )}
      loading={isFetching}
      columns={columnDefinitions}
      rows={data}
      getRowId={(row) => row.jobName}
      editable={false}
      customActions={(id, row) => [
        {
          tooltipTitle: t('delete'),
          icon: 'trash-can',
          onClick: () => handleDelete(row)
        },
        {
          tooltipTitle: t(
            `cronJobs.grid.${row?.state === 'PAUSED' ? 'start' : 'pause'}`
          ),
          icon: row?.state === 'PAUSED' ? 'play' : 'pause',
          onClick: () => handlePauseClick(id, row)
        }
      ]}
      confirmationTitle={t('deleteDialog.title')}
      confirmationBody={t('deleteDialog.body')}
    />
  );
};

export default CronJobsCRUD;
